/**
 * ColorConverter — converteer een kleur live tussen HEX / RGB / HSL / HSV / CMYK.
 * Full-width, gedeelde PageHeader, react-colorful picker + eyedropper + presets.
 * Source of truth = RGB triplet; conversie-helpers in utils/colorConverters.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { ContentCopy as CopyIcon, Casino as RandomIcon, Refresh as ResetIcon } from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import {
  clampColor, rgbToHex, hexToRgb, rgbToHsl, hslToRgb, rgbToHsv, hsvToRgb,
  rgbToCmyk, cmykToRgb, relLuminance, contrastRatio, randomRgb, fmt,
} from '../../utils/colorConverters';

const T = {
  primary: '#526075', primaryDim: '#465469',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#adb3b5',
  shadow: '0 10px 30px rgba(45,51,53,0.06)',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", "Menlo", "Consolas", monospace',
};

const Icon = ({ name, fill, size = 22, sx = {} }) => (
  <span className="material-symbols-outlined"
    style={{ fontSize: size, fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 500`, lineHeight: 1, ...sx }}>{name}</span>
);

const PRESETS = ['#526075', '#635983', '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#0ea5e9', '#111827', '#ffffff'];
const DEFAULT_RGB = { r: 99, g: 89, b: 131 };

export default function ColorConverter() {
  const [rgb, setRgb] = useState(DEFAULT_RGB);

  const hex = useMemo(() => rgbToHex(rgb), [rgb]);
  const hsl = useMemo(() => rgbToHsl(rgb), [rgb]);
  const hsv = useMemo(() => rgbToHsv(rgb), [rgb]);
  const [cmyk, setCmyk] = useState(() => rgbToCmyk(DEFAULT_RGB));

  const lum = useMemo(() => relLuminance(rgb), [rgb]);
  const contrastWhite = useMemo(() => contrastRatio(lum, 1), [lum]);
  const contrastBlack = useMemo(() => contrastRatio(lum, 0), [lum]);
  const onColor = lum > 0.5 ? '#000' : '#fff';

  // Niet-CMYK-wijziging: leidt CMYK opnieuw af. CMYK-wijziging houdt de ingevoerde
  // waarden vast (RGB kan de K niet onthouden, dus CMYK blijft eigen state).
  const commitRgb = (next) => { setRgb(next); setCmyk(rgbToCmyk(next)); };
  const setHex = (v) => { const next = hexToRgb(v); if (next) commitRgb(next); };
  const setRgbField = (key, raw) => commitRgb({ ...rgb, [key]: clampColor(parseInt(raw, 10) || 0, 0, 255) });
  const setHslField = (key, raw) => commitRgb(hslToRgb({ ...hsl, [key]: parseInt(raw, 10) || 0 }));
  const setHsvField = (key, raw) => commitRgb(hsvToRgb({ ...hsv, [key]: parseInt(raw, 10) || 0 }));
  const setCmykField = (key, raw) => { const next = { ...cmyk, [key]: clampColor(parseInt(raw, 10) || 0, 0, 100) }; setCmyk(next); setRgb(cmykToRgb(next)); };

  const reset = () => commitRgb(DEFAULT_RGB);
  const random = () => commitRgb(randomRgb());

  const eyedrop = async () => {
    if (!window.EyeDropper) { toast('Pipet niet ondersteund in deze browser'); return; }
    try { const res = await new window.EyeDropper().open(); setHex(res.sRGBHex); } catch { /* geannuleerd */ }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} gekopieerd`),
      () => toast.error('Kopiëren mislukt'),
    );
  };

  const rgbStr = fmt.rgb(rgb), hslStr = fmt.hsl(hsl), hsvStr = fmt.hsv(hsv), cmykStr = fmt.cmyk(cmyk);

  return (
    <Box sx={{ width: '100%', bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 8 }}>

        <PageHeader
          icon="palette"
          title="Kleur converter"
          subtitle="Live-conversie tussen HEX, RGB, HSL, HSV en CMYK — met picker, pipet en WCAG-contrast."
          accent={{ color: T.tertiary, bg: T.tertiaryContainer }}
          rightActions={
            <>
              <Tooltip title="Pipet (scherm)" arrow>
                <IconButton onClick={eyedrop} sx={{ color: T.outline, '&:hover': { color: T.tertiary } }}>
                  <Icon name="colorize" size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Willekeurige kleur" arrow>
                <IconButton onClick={random} sx={{ color: T.outline, '&:hover': { color: T.tertiary } }}>
                  <RandomIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset" arrow>
                <IconButton onClick={reset} sx={{ color: T.outline, '&:hover': { color: T.tertiary } }}>
                  <ResetIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          }
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' }, gap: 3, alignItems: 'start' }}>

          {/* ── Picker-paneel ── */}
          <Box sx={{ bgcolor: T.surface, borderRadius: '0.9rem', p: 2.5, border: `1px solid ${T.surfaceHigh}`, boxShadow: T.shadow }}>
            <Box sx={{
              '& .react-colorful': { width: '100%', height: 240 },
              '& .react-colorful__saturation': { borderRadius: '0.6rem 0.6rem 0 0' },
              '& .react-colorful__last-control': { borderRadius: '0 0 0.6rem 0.6rem' },
              '& .react-colorful__pointer': { width: 22, height: 22 },
            }}>
              <HexColorPicker color={hex.toLowerCase()} onChange={setHex} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '0.5rem', bgcolor: hex, flexShrink: 0, border: `1px solid ${T.surfaceHigh}` }} />
              <SingleField value={hex} onChange={setHex} placeholder="#635983" mono />
              <Tooltip title="Pipet" arrow>
                <IconButton onClick={eyedrop} sx={{ color: T.outline, '&:hover': { color: T.tertiary } }}><Icon name="colorize" size={18} /></IconButton>
              </Tooltip>
            </Box>

            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outline, mt: 2.5, mb: 1 }}>
              Presets
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {PRESETS.map((c) => (
                <Box key={c} onClick={() => setHex(c)} title={c}
                  sx={{ width: 26, height: 26, borderRadius: '0.4rem', bgcolor: c, cursor: 'pointer', border: `1px solid ${T.surfaceHigh}`, transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.12)' } }} />
              ))}
            </Box>
          </Box>

          {/* ── Rechterzijde ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Swatch-banner */}
            <Box sx={{
              borderRadius: '0.9rem', bgcolor: hex, color: onColor, minHeight: 130,
              boxShadow: '0 18px 40px rgba(45,51,53,0.08)', p: { xs: 2.5, md: 3.5 },
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap',
            }}>
              <Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7 }}>Voorbeeld</Typography>
                <Typography sx={{ fontFamily: T.fontMono, fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 700, lineHeight: 1.05 }}>{hex}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[hex, rgbStr, hslStr].map((s) => (
                  <Box key={s} onClick={() => copy(s, s.split('(')[0].toUpperCase())}
                    sx={{
                      fontFamily: T.fontMono, fontSize: '0.72rem', px: 1.25, py: 0.5, borderRadius: '0.4rem',
                      bgcolor: 'rgba(255,255,255,0.18)', cursor: 'pointer',
                      border: `1px solid ${onColor === '#fff' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)'}`,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                    }}>{s}</Box>
                ))}
              </Box>
            </Box>

            {/* Formats + WCAG */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 2 }}>
              <FormatCard label="HEX" colorAccent="#3b82f6" valueStr={hex} onCopy={() => copy(hex, 'HEX')}>
                <SingleField value={hex} onChange={setHex} placeholder="#635983" mono />
              </FormatCard>
              <FormatCard label="RGB" colorAccent="#ec4899" valueStr={rgbStr} onCopy={() => copy(rgbStr, 'RGB')}>
                <NumberRow>
                  <NumberField label="R" value={rgb.r} max={255} onChange={(v) => setRgbField('r', v)} />
                  <NumberField label="G" value={rgb.g} max={255} onChange={(v) => setRgbField('g', v)} />
                  <NumberField label="B" value={rgb.b} max={255} onChange={(v) => setRgbField('b', v)} />
                </NumberRow>
              </FormatCard>
              <FormatCard label="HSL" colorAccent="#10b981" valueStr={hslStr} onCopy={() => copy(hslStr, 'HSL')}>
                <NumberRow>
                  <NumberField label="H" value={hsl.h} max={360} unit="°" onChange={(v) => setHslField('h', v)} />
                  <NumberField label="S" value={hsl.s} max={100} unit="%" onChange={(v) => setHslField('s', v)} />
                  <NumberField label="L" value={hsl.l} max={100} unit="%" onChange={(v) => setHslField('l', v)} />
                </NumberRow>
              </FormatCard>
              <FormatCard label="HSV" colorAccent="#f59e0b" valueStr={hsvStr} onCopy={() => copy(hsvStr, 'HSV')}>
                <NumberRow>
                  <NumberField label="H" value={hsv.h} max={360} unit="°" onChange={(v) => setHsvField('h', v)} />
                  <NumberField label="S" value={hsv.s} max={100} unit="%" onChange={(v) => setHsvField('s', v)} />
                  <NumberField label="V" value={hsv.v} max={100} unit="%" onChange={(v) => setHsvField('v', v)} />
                </NumberRow>
              </FormatCard>
              <FormatCard label="CMYK" colorAccent="#8b5cf6" valueStr={cmykStr} onCopy={() => copy(cmykStr, 'CMYK')}>
                <NumberRow>
                  <NumberField label="C" value={cmyk.c} max={100} unit="%" onChange={(v) => setCmykField('c', v)} />
                  <NumberField label="M" value={cmyk.m} max={100} unit="%" onChange={(v) => setCmykField('m', v)} />
                  <NumberField label="Y" value={cmyk.y} max={100} unit="%" onChange={(v) => setCmykField('y', v)} />
                  <NumberField label="K" value={cmyk.k} max={100} unit="%" onChange={(v) => setCmykField('k', v)} />
                </NumberRow>
              </FormatCard>

              <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, border: `1px solid ${T.surfaceHigh}`, boxShadow: T.shadow, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: T.outline }}>Contrast (WCAG)</Typography>
                <ContrastRow bg="#fff" fg={hex} ratio={contrastWhite} label="op wit" />
                <ContrastRow bg="#000" fg={hex} ratio={contrastBlack} label="op zwart" />
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.5 }}>
                  Luminantie: <span style={{ fontFamily: T.fontMono, color: T.text }}>{lum.toFixed(3)}</span>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────

function FormatCard({ label, colorAccent, valueStr, onCopy, children }) {
  return (
    <Box sx={{ bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5, border: `1px solid ${T.surfaceHigh}`, boxShadow: T.shadow }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorAccent }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: T.outline }}>{label}</Typography>
        </Box>
        <Tooltip title={`Kopieer ${label}`} arrow>
          <IconButton size="small" onClick={onCopy} sx={{ color: T.outlineVariant, '&:hover': { color: T.tertiary } }}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {children}
      <Typography sx={{ mt: 1.25, fontFamily: T.fontMono, fontSize: '0.72rem', color: T.outline, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{valueStr}</Typography>
    </Box>
  );
}

function SingleField({ value, onChange, placeholder, mono }) {
  const [draft, setDraft] = React.useState(value);
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => { if (!focused) setDraft(value); }, [value, focused]);
  const commit = () => { if (hexToRgb(draft)) onChange(draft); else setDraft(value); };
  return (
    <Box component="input" value={draft} placeholder={placeholder}
      onFocus={() => setFocused(true)} onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      sx={{
        width: '100%', bgcolor: T.surfaceLow, border: 'none', borderRadius: '0.5rem',
        px: 1.5, py: 1.25, color: T.text, outline: 'none',
        fontFamily: mono ? T.fontMono : T.fontBody, fontSize: '1rem', fontWeight: 600,
        '&:focus': { boxShadow: `0 0 0 2px ${T.tertiary}40` },
      }} />
  );
}

function NumberRow({ children }) {
  const count = React.Children.count(children);
  return <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 1 }}>{children}</Box>;
}

function NumberField({ label, value, max, unit, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outlineVariant, textAlign: 'center' }}>{label}{unit ? ` (${unit})` : ''}</Typography>
      <Box component="input" type="number" min={0} max={max} value={value} onChange={(e) => onChange(e.target.value)}
        sx={{
          width: '100%', textAlign: 'center', bgcolor: T.surfaceLow, border: 'none', borderRadius: '0.5rem',
          px: 1, py: 1.25, color: T.text, outline: 'none', fontFamily: T.fontMono, fontSize: '0.95rem', fontWeight: 700,
          '&:focus': { boxShadow: `0 0 0 2px ${T.tertiary}40` },
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
          MozAppearance: 'textfield',
        }} />
    </Box>
  );
}

function ContrastRow({ bg, fg, ratio, label }) {
  const passAA = ratio >= 4.5;
  const passAALarge = ratio >= 3;
  const verdict = passAA ? 'AA' : passAALarge ? 'AA groot' : 'Onvoldoende';
  const verdictColor = passAA ? '#10b981' : passAALarge ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '0.5rem', bgcolor: T.surfaceLow }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '0.4rem', bgcolor: bg, color: fg, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.font, fontSize: '0.82rem', fontWeight: 800, border: bg === '#fff' ? `1px solid ${T.surfaceHigh}` : 'none' }}>Aa</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>{label}</Typography>
        <Typography sx={{ fontFamily: T.fontMono, fontSize: '0.95rem', fontWeight: 700, color: T.text }}>{ratio.toFixed(2)}:1</Typography>
      </Box>
      <Box sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: verdictColor, flexShrink: 0 }}>{verdict}</Box>
    </Box>
  );
}
